(() => {
  const initialized = new WeakSet();

  function ensureInstagramConnections() {
    [
      ['preconnect', 'https://www.instagram.com'],
      ['preconnect', 'https://static.cdninstagram.com'],
      ['dns-prefetch', '//www.instagram.com'],
      ['dns-prefetch', '//static.cdninstagram.com']
    ].forEach(([rel, href]) => {
      if (document.head.querySelector(`link[rel="${rel}"][href="${href}"]`)) return;
      const link = document.createElement('link');
      link.rel = rel;
      link.href = href;
      if (rel === 'preconnect') link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    });
  }

  function buildInstagramEmbedUrl(rawUrl) {
    if (!rawUrl) return '';

    try {
      const parsed = new URL(rawUrl, window.location.origin);
      const hostname = parsed.hostname.replace(/^www\./, '').toLowerCase();
      if (hostname !== 'instagram.com') return '';

      let path = parsed.pathname.replace(/\/+$/, '');
      if (path.endsWith('/embed')) return `https://www.instagram.com${path}/`;

      const supported = /^\/(reel|p|tv)\/[^/]+$/i.test(path);
      if (!supported) return '';

      return `https://www.instagram.com${path}/embed/`;
    } catch (error) {
      return '';
    }
  }

  function mountInstagramFrame(scope) {
    const host = scope?.querySelector('[data-instagram-frame]');
    if (!host || host.dataset.instagramMounted === 'true') return;

    host.dataset.instagramMounted = 'true';
    const loader = host.querySelector('[data-instagram-loader]');
    const embedUrl = buildInstagramEmbedUrl(host.dataset.instagramUrl || '');

    if (!embedUrl) {
      host.classList.add('has-error');
      if (loader) {
        loader.innerHTML = '<strong>Instagram bağlantısını kontrol edin.</strong><small>Reel veya gönderinin herkese açık bağlantısını kullanın.</small>';
      }
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.className = 'loot-instagram-embed__frame';
    iframe.src = embedUrl;
    iframe.title = 'Instagram videosu';
    iframe.loading = 'eager';
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.allow = 'autoplay; clipboard-write; encrypted-media; picture-in-picture';
    iframe.allowFullscreen = true;
    iframe.setAttribute('scrolling', 'no');
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-presentation allow-popups allow-popups-to-escape-sandbox allow-top-navigation-by-user-activation');

    let loaded = false;
    iframe.addEventListener('load', () => {
      loaded = true;
      host.classList.add('is-loaded');
      loader?.remove();
    }, { once: true });

    window.setTimeout(() => {
      if (loaded || !loader) return;
      const text = loader.querySelector('strong');
      if (text) text.textContent = 'Instagram yanıtı bekleniyor…';
    }, 3500);

    host.appendChild(iframe);
  }

  function init(section) {
    if (!section || initialized.has(section)) return;
    initialized.add(section);
    ensureInstagramConnections();

    const rail = section.querySelector('[data-influencer-rail]');
    const modal = section.querySelector('[data-influencer-modal]');
    const content = section.querySelector('[data-influencer-content]');
    const openButtons = section.querySelectorAll('[data-influencer-open]');
    const closeButtons = section.querySelectorAll('[data-influencer-close]');
    const previousProfiles = section.querySelector('[data-influencer-prev]');
    const nextProfiles = section.querySelector('[data-influencer-next]');

    let lastTrigger = null;
    let storyTemplates = [];
    let currentStory = 0;
    let storyMedia = null;
    let storyCaption = null;
    let storyProduct = null;
    let storyInstagram = null;
    let defaultStoryProductLabel = '';
    let storyPrevious = null;
    let storyNext = null;
    let progressItems = [];
    let touchStartX = 0;
    let touchStartY = 0;

    const scrollRail = (direction) => {
      if (!rail) return;
      const amount = Math.max(260, Math.round(rail.clientWidth * .72));
      rail.scrollBy({ left: amount * direction, behavior: 'smooth' });
    };

    previousProfiles?.addEventListener('click', () => scrollRail(-1));
    nextProfiles?.addEventListener('click', () => scrollRail(1));

    const pauseMedia = () => {
      storyMedia?.querySelectorAll('video').forEach((video) => {
        video.pause();
        video.currentTime = 0;
      });
    };

    const clearStory = () => {
      pauseMedia();
      if (storyMedia) storyMedia.innerHTML = '';
    };

    const updateProgress = () => {
      progressItems.forEach((item, index) => {
        item.classList.toggle('is-active', index === currentStory);
        item.classList.toggle('is-complete', index < currentStory);
        item.setAttribute('aria-current', index === currentStory ? 'true' : 'false');
      });

      if (storyPrevious) storyPrevious.disabled = currentStory <= 0;
      if (storyNext) storyNext.disabled = currentStory >= storyTemplates.length - 1;

      const multiple = storyTemplates.length > 1;
      if (storyPrevious) storyPrevious.hidden = !multiple;
      if (storyNext) storyNext.hidden = !multiple;
    };

    const renderStory = (index) => {
      if (!storyTemplates.length || !storyMedia) return;
      currentStory = Math.max(0, Math.min(index, storyTemplates.length - 1));
      const template = storyTemplates[currentStory];
      clearStory();
      storyMedia.appendChild(template.content.cloneNode(true));

      const caption = template.dataset.caption || '';
      if (storyCaption) {
        storyCaption.textContent = caption;
        storyCaption.hidden = !caption;
      }

      const productUrl = template.dataset.productUrl || '';
      const productLabel = template.dataset.productLabel || defaultStoryProductLabel;
      if (storyProduct) {
        storyProduct.hidden = !productUrl;
        if (productUrl) {
          storyProduct.href = productUrl;
          storyProduct.textContent = productLabel || defaultStoryProductLabel;
        }
      }

      const instagramHost = storyMedia.querySelector('[data-instagram-frame]');
      const instagramUrl = instagramHost?.dataset.instagramUrl || '';
      if (storyInstagram) {
        storyInstagram.hidden = !instagramUrl;
        if (instagramUrl) storyInstagram.href = instagramUrl;
      }

      updateProgress();

      const video = storyMedia.querySelector('video');
      if (video) {
        video.addEventListener('ended', () => {
          if (currentStory < storyTemplates.length - 1) renderStory(currentStory + 1);
        }, { once: true });
      }

      mountInstagramFrame(storyMedia);
    };

    const closeModal = () => {
      if (!modal) return;
      modal.classList.remove('is-open');
      modal.setAttribute('aria-hidden', 'true');
      document.body.classList.remove('loot-influencer-lock');
      clearStory();
      if (content) content.innerHTML = '';
      storyTemplates = [];
      currentStory = 0;
      window.setTimeout(() => lastTrigger?.focus(), 30);
    };

    const bindStoryControls = () => {
      storyMedia = content?.querySelector('[data-story-media]');
      storyCaption = content?.querySelector('[data-story-caption]');
      storyProduct = content?.querySelector('[data-story-product]');
      storyInstagram = content?.querySelector('[data-story-instagram]');
      defaultStoryProductLabel = storyProduct?.textContent?.trim() || 'Videodaki Ürünleri İncele';
      storyPrevious = content?.querySelector('[data-story-prev]');
      storyNext = content?.querySelector('[data-story-next]');
      progressItems = Array.from(content?.querySelectorAll('[data-story-progress-item]') || []);
      storyTemplates = Array.from(content?.querySelectorAll('[data-story-template]') || []);

      storyPrevious?.addEventListener('click', () => renderStory(currentStory - 1));
      storyNext?.addEventListener('click', () => renderStory(currentStory + 1));
      progressItems.forEach((item) => {
        item.addEventListener('click', () => renderStory(Number(item.dataset.storyIndex || 0)));
      });

      const stage = content?.querySelector('[data-story-stage]');
      stage?.addEventListener('touchstart', (event) => {
        const touch = event.changedTouches[0];
        touchStartX = touch.clientX;
        touchStartY = touch.clientY;
      }, { passive: true });
      stage?.addEventListener('touchend', (event) => {
        const touch = event.changedTouches[0];
        const deltaX = touch.clientX - touchStartX;
        const deltaY = touch.clientY - touchStartY;
        if (Math.abs(deltaX) < 45 || Math.abs(deltaX) <= Math.abs(deltaY)) return;
        if (deltaX < 0 && currentStory < storyTemplates.length - 1) renderStory(currentStory + 1);
        if (deltaX > 0 && currentStory > 0) renderStory(currentStory - 1);
      }, { passive: true });
    };

    const openModal = (trigger) => {
      if (!modal || !content) return;
      const templateId = trigger.dataset.templateId;
      const template = templateId ? document.getElementById(templateId) : null;
      if (!template) return;

      lastTrigger = trigger;
      content.innerHTML = '';
      content.appendChild(template.content.cloneNode(true));
      bindStoryControls();

      modal.classList.add('is-open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.classList.add('loot-influencer-lock');
      modal.querySelector('[data-influencer-close]')?.focus();
      renderStory(0);
    };

    openButtons.forEach((button) => button.addEventListener('click', () => openModal(button)));
    closeButtons.forEach((button) => button.addEventListener('click', closeModal));

    document.addEventListener('keydown', (event) => {
      if (!modal?.classList.contains('is-open')) return;
      if (event.key === 'Escape') closeModal();
      if (event.key === 'ArrowLeft' && currentStory > 0) renderStory(currentStory - 1);
      if (event.key === 'ArrowRight' && currentStory < storyTemplates.length - 1) renderStory(currentStory + 1);
    });
  }

  function initAll(root = document) {
    root.querySelectorAll('[data-loot-influencers]').forEach(init);
  }

  document.addEventListener('DOMContentLoaded', () => initAll());
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
